import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/api';
import config from '../config/config';
import { Trash2, Edit, AlertTriangle, User } from 'lucide-react';

const AdminButtons = ({ 
  targetUser, 
  onUserDeleted, 
  onUserUpdated,
  className = '', 
  size = 'md' 
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Verificar si el usuario actual es admin
  const isAdmin = config.isAdmin(user);

  // No mostrar los botones si no es admin
  if (!isAdmin) {
    return null;
  }

  // No mostrar botones de admin para el propio usuario admin
  if (targetUser.id === user.id) {
    return null;
  }

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const handleEditClick = () => {
    // Redirigir a Mi Cuenta con el usuario a editar
    navigate('/mi-cuenta', { 
      state: { 
        mode: 'adminEdit',
        editingUserId: targetUser.id,
        editingUser: targetUser
      } 
    });
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await apiService.deleteUser(targetUser.id);
      setShowDeleteModal(false);
      
      // Notificar al componente padre
      if (onUserDeleted) {
        onUserDeleted(targetUser.id);
      }
      
      // Mostrar notificación de éxito
    } catch (error) {
      console.error('Error al eliminar usuario:', error);
      // Mostrar notificación de error
      console.error('Error al eliminar usuario:', error.message);
    } finally {
      setIsDeleting(false);
    }
  };


  const getButtonClasses = () => {
    const baseClasses = `flex items-center justify-center space-x-2 transition-all duration-200 shadow-md hover:shadow-lg`;
    const sizeClasses = {
      sm: 'px-3 py-1.5 text-sm rounded-md',
      md: 'px-4 py-2 text-sm rounded-lg',
      lg: 'px-6 py-3 text-base rounded-lg'
    };
    
    return `${baseClasses} ${sizeClasses[size]} ${className}`;
  };

  return (
    <>
      <div className="flex space-x-2">
        {/* Botón Eliminar */}
        <button
          onClick={handleDeleteClick}
          disabled={isDeleting}
          className={`${getButtonClasses()} bg-red-600 text-white hover:bg-red-700 hover:scale-105 active:scale-95 ${
            isDeleting ? 'opacity-75 cursor-not-allowed' : ''
          }`}
          title="Eliminar usuario"
        >
          {isDeleting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>Eliminando...</span>
            </>
          ) : (
            <>
              <Trash2 className="h-4 w-4" />
              <span>Eliminar</span>
            </>
          )}
        </button>

        {/* Botón Editar */}
        <button
          onClick={handleEditClick}
          className={`${getButtonClasses()} bg-blue-600 text-white hover:bg-blue-700 hover:scale-105 active:scale-95`}
          title="Editar usuario"
        >
          <Edit className="h-4 w-4" />
          <span>Editar</span>
        </button>
      </div>

      {/* Modal de confirmación para eliminar */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full transform transition-all duration-300 scale-100">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="bg-red-100 p-2 rounded-full">
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Eliminar usuario</h3>
              </div>
              <button
                onClick={() => setShowDeleteModal(false)}
                className="text-gray-400 hover:text-gray-600 hover:scale-110 transition-all duration-200"
              >
                ×
              </button>
            </div>

            <div className="p-6">
              <p className="text-gray-600 mb-6">
                ¿Estás seguro de que quieres eliminar a <strong>{targetUser.name}</strong>?
                <br />
                <span className="text-sm text-red-600 font-medium">
                  ⚠️ Esta acción no se puede deshacer.
                </span>
              </p>
              
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 hover:scale-105 active:scale-95 transition-all duration-200"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDelete}
                  disabled={isDeleting}
                  className={`flex-1 px-4 py-2 rounded-lg font-medium transition-all duration-200 shadow-md hover:shadow-lg flex items-center justify-center space-x-2 ${
                    isDeleting 
                      ? 'bg-red-400 text-white cursor-not-allowed' 
                      : 'bg-red-600 text-white hover:bg-red-700 hover:scale-105 active:scale-95'
                  }`}
                >
                  {isDeleting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Eliminando...</span>
                    </>
                  ) : (
                    <span>Sí, eliminar</span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminButtons;
